import { useEffect, useState } from "react";

import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/ko";
import { AiOutlineClose } from "react-icons/ai";

import { fetchVehicleReservationsByDate } from "@/apis/reservation";
import ModalLayout from "@/components/Layout/ModalLayout";
import { daysOfTheWeek } from "@/constants/calendar";
import VehicleDetailModal from "@/pages/Home/components/VehicleDetailModal";
import type { VehicleReservation } from "@/types/vehicleReservation";
import { VEHICLE_STATUS_LABEL } from "@/types/vehicleReservation";
import { cn } from "@/utils/cn";

dayjs.locale("ko");

const statusDot: Record<string, string> = {
  approved: "bg-green-500",
  pending: "bg-yellow-400",
  rejected: "bg-red-500",
  cancelled: "bg-gray-400",
};

interface VehicleDailyModalProps {
  date: Dayjs;
  onClose: () => void;
}

const VehicleDailyModal = ({ date, onClose }: VehicleDailyModalProps) => {
  const [reservations, setReservations] = useState<VehicleReservation[]>([]);
  const [detailItem, setDetailItem] = useState<VehicleReservation | null>(null);

  useEffect(() => {
    const load = async () => {
      const data = await fetchVehicleReservationsByDate(
        date.format("YYYYMMDD"),
      );
      setReservations(data);
    };
    load();
  }, [date]);

  const dateLabel = `${date.format("MM / DD")} (${daysOfTheWeek[date.day()]})`;

  return (
    <ModalLayout variant="primary" onClose={onClose}>
      <div className="flex h-[520px] max-h-[80vh] min-w-[320px] flex-col sm:w-[460px]">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-6 py-4">
          <h3 className="text-base font-bold text-gray-900">
            {dateLabel} 차량 예약 현황
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <AiOutlineClose size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {reservations.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">
              예약 내역이 없습니다.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {reservations.map(r => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setDetailItem(r)}
                  className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 text-left transition-colors hover:border-primary/30 hover:bg-primary/5"
                >
                  <div
                    className={cn(
                      "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                      statusDot[r.status] || "bg-gray-400",
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {r.destination}
                      </span>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                        {VEHICLE_STATUS_LABEL[r.status]}
                      </span>
                    </div>
                    <div className="mt-0.5 text-xs text-gray-500">
                      {r.drivingTime} · {r.purpose}
                      {r.vehicleName && ` · ${r.vehicleName}`}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-400">
                      신청: {r.applicantName}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {detailItem && (
        <VehicleDetailModal
          reservation={detailItem}
          onClose={() => setDetailItem(null)}
        />
      )}
    </ModalLayout>
  );
};

export default VehicleDailyModal;
